import type { App } from "vue";
import { getI18n } from "@/plugins/i18n";
import {
  bindBrowserReachabilityEvents,
  isOffline,
  subscribeReachability,
} from "@/services/offlineSignal";
import useOfflineStore from "@/stores/offline.store";

const BANNER_ID = "alkokh-network-status";

/**
 * Plain-HTTP-compatible connection banner. This is NOT the PWA offline shell:
 * it only tells a user that live data has stopped arriving.
 *
 * Two things changed when offline mode landed:
 *  1. It used to read `navigator.onLine` alone, which is false only when the
 *     machine knows it has no link — so a dead LAN server or a captive portal
 *     showed nothing at all. It now follows the same verdict the data layer
 *     uses (`services/offlineSignal`), which is driven by real request outcomes.
 *  2. The text was hardcoded English on an Arabic-first product.
 */

function translate(key: string, fallback: string): string {
  try {
    const t = getI18n()?.global?.t;
    const text = t ? String(t(key)) : "";

    // vue-i18n echoes the key back when it is missing; never show that.
    return text && text !== key ? text : fallback;
  }
  catch {
    return fallback;
  }
}

function isRtl(): boolean {
  return document.documentElement.getAttribute("dir") === "rtl";
}

/**
 * The till stays silent on purpose. A cashier serving a customer can do nothing
 * about the connection, the sale still completes locally, and a banner over the
 * screen every time the link flickers is pure noise. The POS shows its own
 * quiet sync indicator instead.
 */
function isSilentSurface(): boolean {
  const { pathname, hash } = window.location;
  const route = hash.startsWith("#/") ? hash.slice(1) : pathname;

  return route.startsWith("/pos");
}

function removeBanner() {
  document.getElementById(BANNER_ID)?.remove();
}

function showOfflineBanner() {
  if (document.getElementById(BANNER_ID))
    return;

  const banner = document.createElement("div");

  banner.id = BANNER_ID;
  banner.setAttribute("role", "status");
  banner.setAttribute("dir", isRtl() ? "rtl" : "ltr");
  banner.textContent = translate(
    "alkokh.offline.banner",
    "Connection unavailable. Some live data may not load until the network returns.",
  );
  banner.style.position = "fixed";
  banner.style.insetBlockStart = "calc(8px + env(safe-area-inset-top, 0px))";
  banner.style.insetInline = "12px";
  banner.style.zIndex = "2147483647";
  banner.style.maxWidth = "680px";
  banner.style.marginInline = "auto";
  banner.style.padding = "10px 14px";
  banner.style.borderRadius = "8px";
  banner.style.background = "#fff7ed";
  banner.style.border = "1px solid #fed7aa";
  banner.style.color = "#9a3412";
  banner.style.boxShadow = "0 10px 28px rgba(15, 23, 42, 0.14)";
  banner.style.font = "500 14px/1.4 system-ui, -apple-system, BlinkMacSystemFont, Segoe UI, sans-serif";
  banner.style.textAlign = "center";

  document.body.append(banner);
}

function syncNetworkStatus() {
  if (isOffline() && !isSilentSurface())
    showOfflineBanner();
  else
    removeBanner();
}

export default function (_app: App) {
  if (typeof window === "undefined")
    return;

  bindBrowserReachabilityEvents();

  // Re-evaluate whenever the verdict changes — including when a request fails
  // with the link still "up", which the old listener pair could never see.
  subscribeReachability(syncNetworkStatus);

  // Navigating between a silent surface (the till) and the rest of the app has
  // to re-evaluate too. `popstate`/`hashchange` cover history moves; the timer is
  // the cheap catch-all for pushState navigations, which fire no event.
  window.addEventListener("popstate", syncNetworkStatus);
  window.addEventListener("hashchange", syncNetworkStatus);
  window.setInterval(syncNetworkStatus, 5000);

  // Owns the probing that turns "we stopped being able to reach the server" back
  // into "we can again" without waiting for the user to try something.
  try {
    useOfflineStore().start();
  }
  catch (error) {
    // Pinia not ready (plugin order changed): the banner still works from the
    // signal alone, we just lose active re-probing.
    console.warn("Offline store could not start:", error);
  }

  window.setTimeout(syncNetworkStatus, 0);
}
